// Bot AI difficulty tuning. Central so we can rebalance without touching
// behaviour code. Two catalogues — enemies (progressive threat) and
// friendlies (competent teammates, never designed to be useless).
//
// Rules the numbers enforce:
//   • Easy enemies must let beginners breathe (huge reaction, poor aim, low damage)
//   • Impossible is very difficult but never wallhacks or spawn-kills
//     (see spawnProtectionMs + reactionMs floors)
//   • Friendlies stay useful at every tier, so choosing "Easy" enemies with
//     "Impossible" friendlies is still a fair match — friendlies always fight.

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Every bot has this baseline spawn-protection so freshly-spawned players
// or bots aren't instantly deleted the frame they appear.
export const SPAWN_PROTECTION_MS = 900;

export const ENEMY_DIFFICULTY = {
    easy: {
        label: "Easy",
        description: "Slow, misses often, aims at the body.",
        // Beginner friendly: 1.5s to react, mostly misses, low damage per shot.
        reactionMs: 1500,
        accuracy: 0.18,
        headshotChance: 0.02,
        fireIntervalMs: 900,
        moveSpeed: 2.2,
        engageRange: 16,
        peekChance: 0.05,
        damage: 5,
        aimJitter: 1.4,
        // Aim wanders — looks like new players struggling
        aimWobble: 0.9,
        // Sometimes fires while stationary and misses badly
        stopBeforeFire: true,
        holdAngleMs: 1200,
    },
    normal: {
        label: "Normal",
        description: "Fair reaction, average aim, uses simple cover.",
        reactionMs: 700,
        accuracy: 0.5,
        headshotChance: 0.08,
        fireIntervalMs: 420,
        moveSpeed: 3.2,
        engageRange: 26,
        peekChance: 0.28,
        damage: 10,
        aimJitter: 0.65,
        aimWobble: 0.35,
        stopBeforeFire: false,
        holdAngleMs: 700,
    },
    hard: {
        label: "Hard",
        description: "Faster, more accurate, flanks and peeks.",
        reactionMs: 340,
        accuracy: 0.7,
        headshotChance: 0.14,
        fireIntervalMs: 260,
        moveSpeed: 3.8,
        engageRange: 32,
        peekChance: 0.5,
        damage: 13,
        aimJitter: 0.35,
        aimWobble: 0.15,
        stopBeforeFire: false,
        holdAngleMs: 500,
    },
    expert: {
        label: "Expert",
        description: "Strong aim, positioning, retreats when hurt.",
        reactionMs: 210,
        accuracy: 0.83,
        headshotChance: 0.22,
        fireIntervalMs: 190,
        moveSpeed: 4.3,
        engageRange: 40,
        peekChance: 0.7,
        damage: 16,
        aimJitter: 0.22,
        aimWobble: 0.08,
        stopBeforeFire: false,
        holdAngleMs: 380,
    },
    impossible: {
        label: "Impossible",
        description: "Elite. Deadly, but plays fair — no wallhack, no spawn-kills.",
        // Reaction floor of 120ms so it never fires before you can register
        reactionMs: 120,
        accuracy: 0.92,
        headshotChance: 0.32,
        fireIntervalMs: 130,
        moveSpeed: 4.9,
        engageRange: 55,
        peekChance: 0.85,
        damage: 20,
        aimJitter: 0.12,
        aimWobble: 0.04,
        stopBeforeFire: false,
        holdAngleMs: 300,
    },
};

// Friendly presets — competent at every tier. They lose fights against
// stronger enemies but never willfully sabotage the match. Damage stays
// meaningful so allies actually finish enemies off.
export const FRIENDLY_DIFFICULTY = {
    easy: {
        label: "Easy",
        description: "Loyal but a bit slow — reliable clean-up.",
        reactionMs: 900,
        accuracy: 0.45,
        headshotChance: 0.05,
        fireIntervalMs: 500,
        moveSpeed: 2.8,
        engageRange: 22,
        peekChance: 0.25,
        damage: 9,
        aimJitter: 0.6,
        aimWobble: 0.35,
        stopBeforeFire: false,
        holdAngleMs: 700,
    },
    normal: {
        label: "Normal",
        description: "Balanced teammate — trades fights, follows objectives.",
        reactionMs: 600,
        accuracy: 0.6,
        headshotChance: 0.1,
        fireIntervalMs: 360,
        moveSpeed: 3.3,
        engageRange: 28,
        peekChance: 0.4,
        damage: 11,
        aimJitter: 0.5,
        aimWobble: 0.22,
        stopBeforeFire: false,
        holdAngleMs: 550,
    },
    hard: {
        label: "Hard",
        description: "Sharp support — pushes with you, holds angles.",
        reactionMs: 380,
        accuracy: 0.72,
        headshotChance: 0.15,
        fireIntervalMs: 260,
        moveSpeed: 3.9,
        engageRange: 34,
        peekChance: 0.55,
        damage: 13,
        aimJitter: 0.34,
        aimWobble: 0.14,
        stopBeforeFire: false,
        holdAngleMs: 450,
    },
    expert: {
        label: "Expert",
        description: "Reliable pro teammate — trades hard, flanks smart.",
        reactionMs: 260,
        accuracy: 0.82,
        headshotChance: 0.2,
        fireIntervalMs: 200,
        moveSpeed: 4.2,
        engageRange: 40,
        peekChance: 0.7,
        damage: 15,
        aimJitter: 0.24,
        aimWobble: 0.08,
        stopBeforeFire: false,
        holdAngleMs: 380,
    },
    impossible: {
        label: "Impossible",
        description: "Elite ally. Runs support with pro-level positioning.",
        reactionMs: 160,
        accuracy: 0.9,
        headshotChance: 0.28,
        fireIntervalMs: 150,
        moveSpeed: 4.7,
        engageRange: 48,
        peekChance: 0.8,
        damage: 17,
        aimJitter: 0.15,
        aimWobble: 0.05,
        stopBeforeFire: false,
        holdAngleMs: 300,
    },
};

const DIFFICULTY_ORDER = ["easy", "normal", "hard", "expert", "impossible"];

export function getEnemyDifficulty(id) {
    return ENEMY_DIFFICULTY[id] || ENEMY_DIFFICULTY.normal;
}
export function getFriendlyDifficulty(id) {
    return FRIENDLY_DIFFICULTY[id] || FRIENDLY_DIFFICULTY.normal;
}

// Legacy single-difficulty accessor kept for existing callers.
export function getDifficulty(id) {
    return getEnemyDifficulty(id);
}

export const DIFFICULTY = ENEMY_DIFFICULTY; // legacy export
export const DIFFICULTY_KEYS = DIFFICULTY_ORDER;

// Utility used by AI to add jitter safely
export const aimJitter = (base, jitter) => base * (1 + (Math.random() - 0.5) * clamp(jitter, 0, 2));
